import PropTypes from 'prop-types';
import {
  useForm,
  FormProvider,
  useFormContext,
  useFieldArray,
} from 'react-hook-form';
import { useEffect, useCallback, useState } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import Modal from '@/components/modal/modal-component';
import {
  PrimaryButton,
  PrimaryOutlineButton,
} from '@/components/button/button-component';
import OptionFormFields from '@/pages/company-management/company-options/option-form-fields';
import { useLazyQuery } from '@/hooks/use-api';
import getCompanyPriceListSelect from '@/api/get/config/get-company-price-list-select';
import {
  KEYS_TO_COMPARE,
  createOverridesFromComparison,
} from '../options-utils';
import OptionViewPage from '@/components/option/option-view-page';

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  padding: 20px;
  background-color: ${({ theme }) => theme.COLOR_BG_LIGHT};
  gap: 10px;
`;

const HEADINGS = {
  add: 'Add Variant',
  edit: 'Edit Variant',
  view: 'View Variant',
};

/**
 * Item component for displaying an option menu.
 * @param {object} props - The props passed to the OptionMenu component.
 * @param {object | bool} props.isOpen - Hide or show modal.
 * @param {Function} props.setIsOpen - Set modal state.
 * @param {object} props.editData - Edit data.
 * @param {Function} props.setEditData - Set edit data.
 * @param {Function} props.createVariantDefaults - Function to create default variant data.
 * @param {Function} props.append - Function to append variant data.
 * @param {Function} props.update - Function to update existing variant data.
 * @param {object} props.originalOptions - The original options.
 * @param {string} props.type - Type of variant
 * @returns {JSX.Element} The rendered Item component.
 */
const OptionsVariantModal = ({
  isOpen,
  setIsOpen,
  editData,
  setEditData,
  createVariantDefaults,
  append,
  update,
  originalOptions,
  type,
}) => {
  const [pricelist, setPricelist] = useState([]);
  const methods = useForm({
    defaultValues: {},
  });
  const { getValues, control } = useFormContext();
  const { remove } = useFieldArray({
    control,
    name: 'update',
  });

  const { id: companyId } = useParams();

  const { handleSubmit, reset } = methods;

  const getPricelist = useCallback(
    () => getCompanyPriceListSelect(companyId),
    [companyId],
  );

  const {
    data: pricelistData,
    loading: pricelistLoading,
    refetch,
  } = useLazyQuery(getPricelist);

  // Reset form with default values and fetch price list when modal opens
  useEffect(() => {
    if (isOpen) {
      let defaultValues;

      if (editData?.values) {
        // Edit mode - use the variant data passed from the table
        const variantData = editData.values;
        defaultValues = {
          variant: {
            id: variantData.id || null,
            name: variantData.name || '',
            description: variantData.description || '',
            quote_description: variantData.quote_description || '',
            brochure_link: variantData.brochure_link || '',
            video_link: variantData.video_link || '',
            status: variantData.status || 'active',
            selectedPriceId:
              variantData.selectedPriceId || variantData.price_id || null,
            thumbnail: variantData.thumbnail || null,
            media: variantData.media || null,
            parent_option: variantData.parent_option || null,
            overrides: variantData.overrides || [],
          },
        };
      } else if (createVariantDefaults) {
        // Add mode - use create defaults
        defaultValues = createVariantDefaults();
      }

      if (defaultValues) {
        reset(defaultValues);
      }

      // Trigger the lazy query to fetch price list data
      refetch();
    }
  }, [editData, createVariantDefaults, reset, refetch, isOpen]);

  useEffect(() => {
    if (pricelistData) {
      setPricelist(pricelistData);
    }
  }, [pricelistData]);

  const handleClose = () => {
    setIsOpen(false);
    setEditData(null);
    reset(); // Reset form when closing
  };

  const handleFormSubmit = (values) => {
    const { variant } = values;

    if (variant?.id) {
      const variantData = getValues('originalVariants') || [];
      const findVariant = variantData.find((v) => v.id === variant.id);
      const indexToRemove = variantData.findIndex((v) => v.id === variant.id);

      const hasChanged = KEYS_TO_COMPARE.some(
        (key) => (findVariant[key] ?? null) !== (variant[key] ?? null),
      );

      if (!hasChanged) {
        remove(indexToRemove);
      }
    }

    const findPartNumber = pricelist.find(
      (price) => price.id === variant.selectedPriceId,
    );

    const {
      id: priceId,
      part_number: partNumber,
      temp_pn: tempPn,
    } = findPartNumber;

    const originalValues = createVariantDefaults();
    const originalVariant = originalValues.variant;

    // Create overrides array for keys that differ from original
    const overrides = createOverridesFromComparison(variant, originalVariant);

    const fieldValues = {
      price_id: priceId,
      name: variant.name,
      description: variant.description,
      thumbnail: variant.thumbnail,
      part_number: partNumber,
      quote_description: variant.quote_description,
      brochure_link: variant.brochure_link,
      video_link: variant.video_link,
      parent_option: variant.parent_option ?? null,
      media: variant.media,
      overrides,
      status: variant.status,
      temp_pn: tempPn,
    };

    if (variant.id) {
      fieldValues.id = variant.id;
    }

    if (editData?.values && editData?.index !== undefined) {
      // Edit mode - update existing variant
      update(editData.index, fieldValues);
    } else {
      // Add mode - append new variant
      append(fieldValues);
    }

    handleClose();
  };

  return (
    <Modal
      open={isOpen}
      showClose
      title={type && HEADINGS?.[type] ? HEADINGS[type] : 'Variant'}
      close={handleClose}
      width="90%"
      bodyStyle={{
        display: 'flex',
        flexDirection: 'column',
        padding: '0px',
      }}
    >
      <FormProvider {...methods}>
        {type === 'view' ? (
          <OptionViewPage
            data={{ option: editData?.values }}
            parts={editData?.parts || []}
            variant
          />
        ) : (
          <OptionFormFields
            fieldPrefix="variant"
            isLoading={false}
            pricelistData={pricelistData}
            pricelistLoading={pricelistLoading}
            refetchPriceList={refetch}
            originalOptions={originalOptions}
          />
        )}
      </FormProvider>
      {type !== 'view' && (
        <ButtonContainer>
          <PrimaryOutlineButton
            buttonWidth="108px"
            type="Button"
            loading={false}
            title="Cancel"
            onClick={handleClose}
            margin="0 0 0 0"
          />

          <PrimaryButton
            buttonWidth="108px"
            type="submit"
            loading={false}
            title="Save"
            onClick={handleSubmit(handleFormSubmit)}
          />
        </ButtonContainer>
      )}
    </Modal>
  );
};

OptionsVariantModal.defaultProps = {
  isOpen: false,
  createVariantDefaults: null,
  editData: null,
  originalOptions: {},
  type: null,
};

OptionsVariantModal.propTypes = {
  isOpen: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  setIsOpen: PropTypes.func.isRequired,
  editData: PropTypes.object,
  setEditData: PropTypes.func.isRequired,
  createVariantDefaults: PropTypes.func,
  append: PropTypes.func.isRequired,
  update: PropTypes.func.isRequired,
  originalOptions: PropTypes.object,
  type: PropTypes.string,
};

export default OptionsVariantModal;
